import { Alert } from "antd"
import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import theme from "utils/themeVars"
import { dateTimeFunction } from "../../../utils/date"
import DateTime from "../../../utils/dateTime"
import {
  Button,
  DataTable,
  DetailBar,
  Icon,
  InputNumber,
  Modal,
  MonthPicker,
  SelectInput,
  Space,
  TextInput,
  notification,
} from "../../common"
import { options } from "./constants"
import { createBalloonDateKey, createDateTimeStamp, createInstallmentDateKey, roundPrice } from "./helperFunctions"
import { getCurrencySymbol } from "../../../utils/index"

const InstallmentGeneratorTable = ({
  data,
  paymentInstallments = [],
  possession,
  downPayment,
  onPaymentInstallmentsChange = () => {},
  modalRef,
  startDate,
  installmentMonths = 1,
  totalInstallment,
  totalBalloonPayment,
  frequencyTypeInstallment,
  frequencyTypeBalloon,
  isBallonPaymentRequired,
  setIsPaymentPlanSaved = () => {},
  setIsCustom,
  paymentData,
  constructionBasedPlan,
  instalmentType,
}) => {
  const { t } = useTranslation()
  const [records, setRecords] = useState([])
  const [installmentSum, setInstallmentSum] = useState()
  const initialPlanSize = useRef({ value: 0 })
  const currency = useSelector(
    state =>
      state?.app?.user?.info?.Agent?.AgencySettings?.find(currency => currency?.key === "primary_currency")?.currencyCode
  )

  const installmentType =
    frequencyTypeInstallment === "annually"
      ? 12
      : frequencyTypeInstallment === "bi_annually"
      ? 6
      : frequencyTypeInstallment === "quarterly"
      ? 3
      : 1
  const ballonPaymentType = frequencyTypeBalloon === "annually" ? 12 : frequencyTypeBalloon === "bi_annually" ? 6 : 3

  const installments = installmentMonths / installmentType
  const balloonPayments = Math.floor(installmentMonths / ballonPaymentType) || 1 // incase the balloonPaymentType is greater then the number of months
  const installmentAmount = Math.round(totalInstallment / installments)
  const balloonAmount = Math.round(isBallonPaymentRequired ? totalBalloonPayment / balloonPayments : 0)

  const columns = useMemo(
    () => [
      { title: t("#"), dataIndex: "index", width: 150 },
      ...(instalmentType === "time_based"
        ? [
            { title: t("TYPE"), dataIndex: "type", width: 150 },
            { title: t("MONTH"), dataIndex: "date", width: 180 },
          ]
        : [
            { title: t("MILESTONE"), dataIndex: "milestoneName", width: 150 },
            { title: t("PERCENTAGE"), dataIndex: "milestonePercentage", width: 180 },
          ]),

      {
        title: (
          <>
            {t("AMOUNT")} ({getCurrencySymbol()})
          </>
        ),
        dataIndex: "amount",
      },
      {
        title: "",
        dataIndex: "actions",
        render: (_, record) => <Button onClick={() => {}}>{t("Delete")}</Button>,
      },
    ],
    [data, instalmentType]
  )

  const paymentPlanHeaders = useMemo(
    () => [
      {
        label: t("Booking Price"),
        value: (
          <>
            {getCurrencySymbol()} {roundPrice(paymentData?.bookingPrice, false, "")}
          </>
        ),
      },
      {
        label: t("Down Payment Target"),
        value: (
          <>
            {getCurrencySymbol()} {roundPrice(paymentData?.downPayment, false, "")}
          </>
        ),
      },
      {
        label: t("Possession Charges"),
        value: (
          <>
            {getCurrencySymbol()} {roundPrice(paymentData?.possessionCharges, false, "")}
          </>
        ),
      },
      {
        label: `${t("Total Instalments")}`,
        value: initialPlanSize.current.value || records?.length,
      },
    ],
    [paymentData, t, currency, initialPlanSize.current.value, records]
  )

  const getPossessionMonths = useMemo(() => {
    let possesionTempDate = dateTimeFunction({ date: startDate, Default: true })

    if (frequencyTypeInstallment === "quarterly") {
      return DateTime.getQuarterYearAfterDateProvided(possesionTempDate, installments - 1)
    } else if (frequencyTypeInstallment === "semi-annual") {
      return DateTime.getMonthsAfterDateProvided(possesionTempDate, (installments - 1) * 6)
    }

    return DateTime.getMonthsAfterDateProvided(possesionTempDate, installments - 1)
  }, [startDate, installments, frequencyTypeInstallment])

  const timeBasedInstallmentRecord = possession => {
    let balloonDate = DateTime.getMonthsBeforeDateProvided(startDate, 1)
    initialPlanSize.current.value = 0
    let paymentMap = {}
    paymentMap[0] = {
      key: "-",
      index: null,
      type: "downPayment",
      amount: parseInt(downPayment),
    }

    if (paymentInstallments?.length) {
      let previousNode = { prevIndex: 0, nextIndex: 0, index: 0 }

      paymentInstallments.forEach((e, i) => {
        let dateKey = createDateTimeStamp(DateTime.getDateAndTime(e?.date))

        previousNode.nextIndex = dateKey

        paymentMap[dateKey] = {
          index: dateKey,
          type: e?.type?.toString(),
          date: dateTimeFunction({ date: e?.date }),
          amount: e?.amount,
          prevIndex: previousNode.index ?? 0,
        }

        previousNode = paymentMap[dateKey]
      })
    } else {
      for (let i = 0; i < installmentMonths; i++) {
        let tempDateInstallment = null
        let tempDateBalloon = null
        let installmentKey = null
        let balloonKey = null

        if (i % installmentType === 0) {
          tempDateInstallment = createInstallmentDateKey({
            index: i / installmentType,
            frequencyTypeInstallment: frequencyTypeInstallment,
            startDate: startDate,
          })
          installmentKey = createDateTimeStamp(tempDateInstallment)

          if (paymentMap[installmentKey]) {
            paymentMap[installmentKey].amount += installmentAmount
          } else {
            paymentMap[installmentKey] = {
              index: installmentKey,
              key: i,
              type: "installment",
              date: tempDateInstallment,
              amount: installmentAmount,
            }
          }
          initialPlanSize.current.value++
        }

        if (i !== 0 && (i + 1) % ballonPaymentType === 0 && i + 1 <= installmentMonths && isBallonPaymentRequired) {
          tempDateBalloon = createBalloonDateKey({
            index: (i + 1) / ballonPaymentType,
            frequencyTypeBalloon: frequencyTypeBalloon,
            balloonDate: balloonDate,
          })
          balloonKey = createDateTimeStamp(tempDateBalloon)

          if (paymentMap[balloonKey]) {
            paymentMap[balloonKey].amount += balloonAmount
            paymentMap[balloonKey].type = "balloon_payment"
          } else {
            paymentMap[balloonKey] = {
              index: balloonKey,
              key: i,
              type: "balloon_payment",
              date: tempDateBalloon,
              amount: balloonAmount,
            }
            initialPlanSize.current.value++
          }
        }
      }
    }

    let previousNode = { prevIndex: 0, nextIndex: 0, index: 0 }

    // linked list implementation, created next and previous pointer based on the time for each object
    Object.keys(paymentMap).forEach(key => {
      previousNode.nextIndex = key
      paymentMap[key].prevIndex = previousNode.index ?? 0
      previousNode = paymentMap[key]
    })

    paymentMap[999999999999] = {
      key: "-",
      index: 99999999999999,
      type: "possession",
      amount: roundPrice(possession),
    }
    return paymentMap
  }
  const constructionBasedInstallmentRecord = () => {
    let rows = []
    const installmentData = paymentInstallments?.length ? paymentInstallments : constructionBasedPlan

    installmentData?.forEach((e, i) => {
      rows.push({
        index: i + 1,
        milestoneName: e?.milestoneName,
        milestonePercentage: e?.milestonePercentage,
        amount: paymentInstallments?.length
          ? e?.amount
          : ((e?.milestonePercentage / 100) * installmentAmount)?.toFixed(2),
      })
    })
    return rows
  }
  const fetchInitialRecords = possession => {
    if (instalmentType == "time_based") {
      setRecords(timeBasedInstallmentRecord(possession))
    } else {
      setRecords(constructionBasedInstallmentRecord())
    }
  }

  const handleFieldChange = (index, field, value) => {
    instalmentType == "time_based"
      ? setRecords(prevRecords => {
          prevRecords[index][field] = value
          return { ...prevRecords }
        })
      : setRecords(prevRecords => {
          const updatedRecords = [...prevRecords]
          updatedRecords[index][field] = value
          return updatedRecords
        })
  }

  const handleDeleteRow = obj => {
    if (instalmentType == "time_based") {
      let previousObj = obj?.prevIndex
      let nextObj = obj?.nextIndex

      setRecords(prevRecords => {
        let changedObj = nextObj || previousObj

        prevRecords[changedObj].type = "balloon_payment"
        prevRecords[changedObj].amount += obj.amount

        // re-attach the pointers to keep the structure valid
        if (nextObj) {
          prevRecords[previousObj].nextIndex = nextObj
          prevRecords[nextObj].prevIndex = previousObj
        } else {
          prevRecords[previousObj].nextIndex = null
        }

        delete prevRecords[obj.index]

        return { ...prevRecords }
      })
    } else {
      setRecords(prevRecords => {
        const updatedRecords = [...prevRecords]
        updatedRecords?.splice(obj, 1)
        return updatedRecords
      })
    }
  }

  const handleMonthChange = useCallback(
    (value, current) => {
      let newDateKey = createDateTimeStamp(DateTime.getDateAndTime(value))
      let oldDateKey = current?.date ? createDateTimeStamp(current?.date) : current?.index

      if (newDateKey < createDateTimeStamp(startDate) || newDateKey > createDateTimeStamp(getPossessionMonths)) {
        notification.error({
          message: t("Please add the month that is between the possession and cdp month"),
        })

        return null
      }

      setRecords(prev => {
        if (prev[newDateKey]) {
          prev[newDateKey].amount += prev[oldDateKey].amount
          prev[newDateKey].type = "balloon_payment"
          // this is the case where the node is being removed from its flow, the previous node and the next node of the swapped node need to be connected
          if (prev[oldDateKey].prevIndex) {
            prev[prev[oldDateKey].prevIndex].nextIndex = prev[oldDateKey].nextIndex
          }
          if (prev[oldDateKey].nextIndex) {
            prev[prev[oldDateKey].nextIndex].prevIndex = prev[oldDateKey].prevIndex
          }

          delete prev[oldDateKey]
          return { ...prev }
        }

        prev[newDateKey] = { ...prev[oldDateKey], date: DateTime.getDateAndTime(value), index: newDateKey }
        delete prev[oldDateKey]

        // TODO: this implementation can be improved with binary search (o log n)
        Object.keys(prev).sort()

        let previousNode = { prevIndex: 0, nextIndex: 0, index: 0 }

        Object.keys(prev).forEach((key, index) => {
          previousNode.nextIndex = key
          prev[key].prevIndex = previousNode.index ?? 0
          previousNode = prev[key]
        })

        return { ...prev }
      })
    },
    [startDate, getPossessionMonths, t]
  )

  const onAddRow = useCallback(
    ({ convertedMapToArray }) => {
      setRecords(prev => {
        let previousObject = convertedMapToArray[convertedMapToArray?.length - 2]
        let newIndex = DateTime.convertDateToUnixTimeStamp(
          DateTime.getMonthsAfterDateProvided(getPossessionMonths, convertedMapToArray?.length)
        )
        previousObject.nextIndex = newIndex

        prev[newIndex] = {
          index: newIndex,
          key: newIndex,
          type: "installment",
          date: null,
          amount: 0,
          prevIndex: previousObject.index,
          nextIndex: null,
        }
        return { ...prev }
      })
    },
    [getPossessionMonths]
  )
  const getInstallmentSum = () => {
    return Math.round(installmentAmount * installments + balloonAmount * balloonPayments)
  }
  const getRemainingInstallmentAmount = () => {
    return Math.round(getInstallmentSum() - installmentSum)
  }

  const tableData = useMemo(() => {
    let convertedMapToArray = Object.values(records)
    const tableRecords =
      instalmentType == "time_based"
        ? convertedMapToArray.map((e, i) => {
            if (i === 0) {
              return {
                index: "-",
                type: <SelectInput value={options().filter(e => e?.value === "downPayment")} disabled />,
                date: <MonthPicker value={DateTime.getDateAndTime(startDate)} format="MMM - YYYY" disabled />,
                amount: <TextInput value={e?.amount} suffixText={getCurrencySymbol()} disabled />,
              }
            } else if (i === convertedMapToArray?.length - 1) {
              return {
                index: "-",
                type: <SelectInput value={options().filter(e => e?.value === "possession")} disabled />,
                date: <MonthPicker value={getPossessionMonths} format="MMM - YYYY" disabled />,
                amount: <TextInput value={possession} suffixText={getCurrencySymbol()} disabled />,
              }
            } else {
              return {
                index: i,
                type: (
                  <SelectInput
                    options={options().filter(
                      option => option.value === "installment" || option.value === "balloon_payment"
                    )}
                    value={e?.type}
                    onChange={el => handleFieldChange(e?.index, "type", el)}
                  />
                ),
                date: (
                  <MonthPicker
                    noCurrentMonth={true}
                    value={e?.date || null}
                    format="MMM - YYYY"
                    onChange={value => handleMonthChange(value, e)}
                    disabledDate={date => {
                      return date && dayjs(date).isBefore(dayjs(), "day")
                    }}
                  />
                ),
                amount: (
                  <TextInput
                    value={e?.amount}
                    suffixText={getCurrencySymbol()}
                    onChange={el =>
                      handleFieldChange(e?.index, "amount", parseInt((el.target.value || "0").replace(/,/g, ""), 10))
                    }
                    errorMsg={
                      i === convertedMapToArray?.length - 2 &&
                      !!installmentSum &&
                      Math.round(installmentSum) !== getInstallmentSum() &&
                      getRemainingInstallmentAmount() > 0
                        ? `${t(
                            "The remaining amount needed to complete the plan is"
                          )} ${currency} ${getRemainingInstallmentAmount()}`
                        : undefined
                    }
                  />
                ),
                actions: (
                  <Space className="w-100" align="center" style={{ justifyContent: "space-between" }}>
                    {convertedMapToArray?.length > 3 && (
                      <Icon
                        icon="FiTrash2"
                        size="1.36rem"
                        type="light"
                        color={theme["error-color"]}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDeleteRow(e)}
                      />
                    )}
                    {convertedMapToArray.length - 2 < initialPlanSize.current.value &&
                      i === convertedMapToArray.length - 2 && (
                        <Button
                          icon="MdAdd"
                          size="small"
                          type="link"
                          onClick={() => onAddRow({ convertedMapToArray: convertedMapToArray })}
                        >
                          {t("Add New Row")}
                        </Button>
                      )}
                  </Space>
                ),
              }
            }
          })
        : Array.isArray(records) &&
          records.map((e, i) => ({
            index: i + 1,
            milestoneName: e?.milestoneName,
            milestonePercentage: (
              <InputNumber
                value={e?.milestonePercentage}
                onChange={val => {
                  handleFieldChange(i, "milestonePercentage", val)
                  handleFieldChange(i, "amount", ((val / 100) * installmentAmount)?.toFixed(2))
                }}
              />
            ),
            amount: e?.amount,
            actions: (
              <Icon
                icon="FiTrash2"
                size="1.36rem"
                type="light"
                color={theme["error-color"]}
                style={{ cursor: "pointer" }}
                onClick={() => handleDeleteRow(parseInt(i))}
              />
            ),
          }))

    return tableRecords
  }, [
    records,
    startDate,
    currency,
    data?.unitCurrency,
    getPossessionMonths,
    handleMonthChange,
    onAddRow,
    possession,
    t,
    instalmentType,
    installmentSum,
  ])

  useEffect(() => {
    let isCustom = false
    let sum = 0

    if (instalmentType == "time_based") {
      Object.keys(records).forEach(key => {
        const record = records[key]
        const amount = +record.amount

        if (
          (record.type === "balloon_payment" &&
            amount !== balloonAmount &&
            amount !== installmentAmount + balloonAmount) ||
          (record.type === "installment" && amount !== installmentAmount)
        ) {
          isCustom = true
        }
        if (record.type === "balloon_payment" || record.type === "installment") sum += amount || 0
      })
    } else {
      for (let i = 0; i < records?.length; i++) {
        if (records?.[i]?.milestonePercentage != constructionBasedPlan?.[i]?.milestonePercentage) {
          isCustom = true
        }
        sum += +records?.[i].amount || 0
      }
    }

    setInstallmentSum(sum)
    setIsCustom(isCustom)
  }, [tableData])

  useEffect(() => {
    onPaymentInstallmentsChange(paymentInstallments)
  }, [paymentInstallments, onPaymentInstallmentsChange])

  const onModalVisible = () => {
    setRecords([])
    fetchInitialRecords(possession)
  }

  const onSavePaymentPlan = () => {
    let convertedMapToArray = Object.values(records)
    const updatedPaymentPlan = instalmentType == "time_based" ? convertedMapToArray?.slice(1, -1) : records
    onPaymentInstallmentsChange(updatedPaymentPlan)
    notification.success({ message: t("Payment plan saved successfully") })
    modalRef?.current?.setVisible(false)
    setIsPaymentPlanSaved?.(true)
  }

  return (
    <Modal
      ref={modalRef}
      width={900}
      title={t("Payment Plan")}
      okText={t("Save")}
      onOk={onSavePaymentPlan}
      onCancel={() => {
        setRecords([])
        onPaymentInstallmentsChange([])
        modalRef?.current?.setVisible(false)
      }}
      disableOkButton={
        !!installmentSum &&
        Math.round(installmentSum) !== Math.round(installmentAmount * installments + balloonAmount * balloonPayments)
      }
      onModalVisible={onModalVisible}
      destroyOnClose
    >
      <DetailBar detailData={paymentPlanHeaders} />
      <div>&nbsp;</div>
      <DataTable stickyOffset={-16} data={tableData} columns={columns} scroll={{ y: 600 }} className="mbe-8" />

      {!!installmentSum &&
        Math.round(installmentSum) !==
          Math.round(installmentAmount * installments + balloonAmount * balloonPayments) && (
          <Alert
            type="error"
            message={`${
              instalmentType == "time_based"
                ? t("Total instalment and balloon payments should be equal to")
                : t("Total instalment payments should be equal to")
            } ${currency} ${Math.round(installmentAmount * installments + balloonAmount * balloonPayments, false, "")}`}
          />
        )}
    </Modal>
  )
}
export default InstallmentGeneratorTable
